import { keepPreviousData } from "@tanstack/react-query";

import { useListProvisionedSubscriptions } from "../api/generated/subscription-provisioning/subscription-provisioning";
import { toProvisionedSubscriptionListItem } from "../functions/provisionedSubscription";

/**
 * The response as it reads before the query resolves and on error. Frozen and shared so no consumer
 * needs a null check.
 */
const EMPTY_LIST = Object.freeze( {
	items: Object.freeze( [] ),
	meta: Object.freeze( { total: 0, page: 1, limit: 25, totalPages: 0 } ),
} );

/**
 * One page of the provisioner's own subscriptions, from `GET provisioning/subscriptions`.
 *
 * `query` is sent only when `q` is non-empty: an empty `query=` would still be a different query key, so
 * clearing the search would refetch rather than reuse the unfiltered page already in the cache.
 *
 * @param {Object} params The list parameters.
 * @param {string} [params.q]     The customer-email search, matched case-insensitively as "contains".
 * @param {number} [params.page]  The page number, starting at 1.
 * @param {number} [params.limit] Rows per page, 1 to 100.
 *
 * @returns {{items: Object[], meta: Object, isLoading: boolean, isError: boolean, isPlaceholderData: boolean}}
 *          The page and the query state.
 */
export default function useProvisionedSubscriptionList( { q, page, limit } = {} ) {
	const params = {
		...( q ? { query: q } : {} ),
		...( page === undefined ? {} : { page } ),
		...( limit === undefined ? {} : { limit } ),
	};

	const { data, isLoading, isError, isPlaceholderData } = useListProvisionedSubscriptions( params, {
		query: {
			// Every parameter is part of the query key, so each page change and each committed search is a
			// cache miss that would otherwise unmount the table into a loading state.
			placeholderData: keepPreviousData,
		},
	} );

	return {
		items: data ? data.items.map( toProvisionedSubscriptionListItem ) : EMPTY_LIST.items,
		meta: data ? data.meta : EMPTY_LIST.meta,
		isLoading,
		isError,
		isPlaceholderData,
	};
}
